import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { EDITOR_TOOLS, MODAL_TYPES } from '@editor/config/constants'
import { INITIAL_SHAPE_STATE } from '@editor/contexts/Editor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { EditorConfig } from '@editor/interfaces/editor'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { Editor, InspectionItemType, ShapeKeyType, VolumeEstimationMethods } from '@editor/interfaces/interfaces'
import { PlaneSide } from '@editor/interfaces/shape'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import useDetectPolygonsVolume from '@editor/pages/projects/editor/tools/VolumeEstimation/Polygon/hooks/useDetectPolygonsVolume'
import { useInspectionItems } from '@editor/pages/projects/hooks/useInspectionItems'
import { COMMON_MESSAGE, ERROR_PROCESS, processErrorHandler } from '@editor/services/ErrorHandler'
import { withPromiseContained } from '@editor/services/Util'
import {
  VOLUME_ESTIMATION_IN_PROGRESS_TEXT,
  detectPolygonsVolume,
  pollAsyncJobStatus,
  reevaluateVolumeEstimation,
} from '@editor/services/VolumeEstimation'
import { GenericError } from '@editor/services/errors/GenericError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems } from '@editor/store/selectors'
import { addSavingElementIds, addWorkingTool, removeSavingElementIds, removeWorkingTool } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import * as Sentry from '@sentry/react'
import { Matrix4, Vector3 } from 'three'

import { Mode } from '../../utils'
import {
  DrawingStage,
  reset,
  resetWorkingPoints,
  setIsSaving,
  setIsUpdating,
  setRebarModelledConfirmed,
} from '../store/volumeEstimation'

const WORKING_IDS = ['panel-volume-working']

export default function useEditor(props: Editor): EditorConfig {
  // Toast
  const successToastIdRef = useRef<string>(undefined)
  const infoToastIdRef = useRef<string>(undefined)

  // Props
  const { selectedTool, changeTool, isPreviousTool, changeIsJobRunning } = props

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showModal, showErrorModal } = useContext(GlobalModalContext)

  // Hooks
  const { fetchInspectionItems } = useInspectionItems()

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((root) => root.page.project)
  const inspectionArea = useAppSelector((root) => root.page.inspectionArea)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const inspectionItems = useAppSelector(selectInspectionItems)

  const jobRunningTool = useAppSelector((state) => state.editor.jobRunningTool)
  const selectedElementIds = useAppSelector((state) => state.toolUtil.core.selectedElementIds)

  const isSaving = useAppSelector((root) => root.toolVolumeEstimationPolygon.isSaving)
  const isUpdating = useAppSelector((root) => root.toolVolumeEstimationPolygon.isUpdating)
  const isDirty = useAppSelector((root) => root.toolVolumeEstimationPolygon.isDirty)
  const hasWorkingPoints = useAppSelector((state) => state.toolVolumeEstimationPolygon.workingPoints.length > 0)
  const planes = useAppSelector((root) => root.toolVolumeEstimationPolygon.planes)
  const rebarModelledConfirmed = useAppSelector((root) => root.toolVolumeEstimationPolygon.rebarModelledConfirmed)
  const drawingStage = useAppSelector((root) => root.toolVolumeEstimationPolygon.drawingStage)
  const isPolygonClosable = useAppSelector(
    (root) =>
      root.toolVolumeEstimationPolygon.drawingStage === DrawingStage.Draw &&
      root.toolVolumeEstimationPolygon.workingPoints.length >= 3,
  )
  const estimationMethod = useAppSelector((root) => root.toolVolumeEstimationPolygon.estimationMethod)
  const planeCreationOrder = useAppSelector((state) => state.toolVolumeEstimationPolygon.planeCreationOrder)
  const virtualPlaneTransformation = useAppSelector(
    (root) => root.toolVolumeEstimationPolygon.virtualPlaneTransformation,
  )

  const maskRegions = useAppSelector((root) => root.maskPCD.regions)

  // Flags
  const isSaveable = !(
    planes.upper === null ||
    planes.lower === null ||
    isSaving ||
    (estimationMethod === VolumeEstimationMethods.Advanced && !rebarModelledConfirmed)
  )
  const isDrawing = drawingStage === DrawingStage.Draw
  const isTopVirtual = planeCreationOrder[1] === PlaneSide.UPPER
  const isToolSelected = useMemo(() => selectedTool === EDITOR_TOOLS.VOLUME_POLYGON, [selectedTool])
  const selectedVolumes = useMemo(
    () =>
      inspectionItems.items.filter(
        (item) =>
          item.item_type === InspectionItemType.Volume &&
          selectedElementIds.find((id) => item.shape_ids.polygons.includes(id)),
      ),
    [selectedElementIds, inspectionItems],
  )
  const selectedVolume = selectedVolumes[0]
  const isSelectedMultiple = selectedVolumes.length > 1
  const mode = useMemo(() => {
    if (isToolSelected || isSaving) return Mode.Modelling
    if (selectedVolume && selectedTool === EDITOR_TOOLS.MOVE && jobRunningTool === '') return Mode.Reevaluation
    return Mode.None
  }, [isToolSelected, isSaving, selectedTool, selectedVolume, jobRunningTool])

  // Hooks
  const { poll } = useDetectPolygonsVolume(ShapeKeyType.POLYGON)

  /**
   * Toggle info panels when tool is selected
   */
  useEffect(() => {
    if (!isToolSelected) {
      if (isPreviousTool(EDITOR_TOOLS.VOLUME_POLYGON) && selectedTool !== EDITOR_TOOLS.FOCUS) {
        dispatch(resetWorkingPoints())
      }
    }
  }, [isToolSelected, selectedTool, isPreviousTool, planes.upper, planes.lower, dispatch])

  /**
   * Reset on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  /**
   * Handle save operation.
   */
  const handleSave = useCallback(async () => {
    if (
      !project ||
      inspectionArea?.downsample_status?.status !== FileProcessingStatus.SUCCEEDED ||
      !inspectionSheet?.inspection_sheet_id ||
      !planes.upper ||
      !planes.lower ||
      planes.upper.points.length < 3 ||
      planes.lower.points.length < 3
    )
      return

    try {
      const token = await getAccessToken()
      if (!token) return

      changeIsJobRunning(true)
      dispatch(addWorkingTool(EDITOR_TOOLS.VOLUME_POLYGON))
      dispatch(setIsSaving(true))
      dispatch(addSavingElementIds(WORKING_IDS))

      let upperPoints = planes.upper.points

      // Apply transformation to upper plane points if it's virtual
      if (isTopVirtual && virtualPlaneTransformation) {
        const matrix = new Matrix4().fromArray(virtualPlaneTransformation)
        const tempVector = new Vector3()
        upperPoints = upperPoints.map((pt) => tempVector.set(pt[0], pt[1], pt[2]).applyMatrix4(matrix).toArray())
      }

      const result = await detectPolygonsVolume(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        inspectionSheet.inspection_sheet_id,
        ShapeKeyType.POLYGON,
        {
          ...INITIAL_SHAPE_STATE(),
          polygons: [
            {
              points: planes.lower.points,
              plane_side: PlaneSide.LOWER,
            },
            {
              points: upperPoints,
              plane_side: PlaneSide.UPPER,
              is_virtual: true, // Upper plane is always virtual to enhance speed and keep dimensions
            },
          ],
        },
        maskRegions.filter((region) => !region.invisible),
        estimationMethod,
        showErrorModal,
      )

      if (result) {
        const job = await poll(result)
        if (!job) return
      }

      if (successToastIdRef.current) toaster.dismiss(String(successToastIdRef.current))
      successToastIdRef.current = toaster.create({
        title: '体積測定が完了しました',
        type: 'success',
        duration: 5000,
        closable: true,
      })

      dispatch(reset())
    } catch (error) {
      Sentry.captureException(error)
      showErrorModal(error instanceof GenericError ? error.message : COMMON_MESSAGE)
    } finally {
      changeIsJobRunning(false)
      dispatch(setIsSaving(false))
      dispatch(removeWorkingTool(EDITOR_TOOLS.VOLUME_POLYGON))
      dispatch(removeSavingElementIds(WORKING_IDS))
    }
  }, [
    project,
    inspectionArea,
    inspectionSheet,
    planes,
    isTopVirtual,
    virtualPlaneTransformation,
    maskRegions,
    estimationMethod,
    showErrorModal,
    poll,
    dispatch,
    changeIsJobRunning,
    getAccessToken,
  ])

  /**
   * Handle reset operation.
   */
  const handleReset = () => {
    dispatch(reset())
  }

  /**
   * Handle apply operation.
   */
  const handleApply = async () => {
    dispatch(setAttentionText({ message: VOLUME_ESTIMATION_IN_PROGRESS_TEXT }))
    await handleSave()
    dispatch(setAttentionText({ message: '' }))
  }

  /**
   * Handle submit operation.
   * It's basically save + switch to MOVE/SELECT tool.
   */
  const handleSubmit = () => {
    // Switch to MOVE tool
    changeTool(EDITOR_TOOLS.MOVE, !hasWorkingPoints)

    if (isSaveable) {
      if (infoToastIdRef.current) toaster.dismiss(String(infoToastIdRef.current))
      infoToastIdRef.current = toaster.create({
        title: '体積測定を開始しました',
        description:
          '体積測定が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
        type: 'info',
        duration: 5000,
        closable: true,
      })

      void handleSave()
    }
  }

  /**
   * Submit button for creating a new volume
   */
  const buttonsCreate = {
    apply: {
      key: 'apply-polygon',
      label: '測定',
      loadingLabel: '測定中',
      isLoading: () => isSaving,
      isShown: () => isToolSelected,
      isDisabled: () => !isSaveable,
      onClick: handleApply,
    },
    submit: {
      key: 'save-polygon',
      label: isDirty ? '測定して終了' : undefined,
      loadingLabel: '測定中',
      isLoading: () => false,
      isShown: () => isToolSelected,
      isDisabled: () => isDirty && !isSaveable,
      onClick: handleSubmit,
    },
    complete: {
      isShown: () => isDrawing,
      isDisabled: () => !isPolygonClosable,
    },
    undo: {
      isShown: () => isToolSelected,
      isDisabled: () => isSaving || !isDirty,
    },
    cancel: {
      onClick: () => {
        showModal({
          title: `リセット`,
          body: '変更をリセットしますか？',
          confirmText: 'リセット',
          modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
          onConfirm: () => {
            handleReset()
            return true
          },
        })
      },
      isShown: () => isToolSelected && isDrawing,
      isDisabled: () => isSaving || !isDirty,
    },
    reset: {
      onClick: handleReset,
      isShown: () => isToolSelected,
      isDisabled: () => isSaving || !isDirty,
    },
  }

  /**
   * Submit button for reevaluating an existing volume.
   */
  const buttonsUpdate = {
    apply: {
      key: 'save-polygon',
      label: '体積を再測定',
      loadingLabel: '体積を再測定中',
      isLoading: () => isUpdating,
      isShown: () => mode === Mode.Reevaluation,
      isDisabled: () =>
        !(
          estimationMethod === VolumeEstimationMethods.Standard ||
          (estimationMethod === VolumeEstimationMethods.Advanced && rebarModelledConfirmed)
        ) ||
        isUpdating ||
        isSelectedMultiple,
      onClick: useCallback(async () => {
        if (
          !selectedVolume ||
          !project ||
          !inspectionArea ||
          !inspectionSheet ||
          (estimationMethod === VolumeEstimationMethods.Advanced && !rebarModelledConfirmed)
        )
          return

        const token = await getAccessToken()
        if (!token) return

        dispatch(setIsUpdating(true))
        dispatch(setAttentionText({ message: '体積の再評価中...' }))
        await withPromiseContained(async () => {
          const jobToken = await reevaluateVolumeEstimation(
            token,
            project.project_id,
            inspectionArea.inspection_area_id,
            inspectionSheet.inspection_sheet_id,
            selectedVolume.inspection_item_id!,
            estimationMethod,
            showErrorModal,
          )
          if (!jobToken) return

          const result = await pollAsyncJobStatus(token, jobToken)
          if (!result) return

          await fetchInspectionItems()
        })
          .catch((err) => {
            processErrorHandler(err, ERROR_PROCESS.ESTIMATE_VOLUME, showErrorModal)
            Sentry.captureException(err)
          })
          .finally(() => {
            dispatch(setIsUpdating(false))
            dispatch(setAttentionText({ message: '' }))
            dispatch(setRebarModelledConfirmed(false))
            changeIsJobRunning(false)
          })
      }, [
        selectedVolume,
        estimationMethod,
        rebarModelledConfirmed,
        project,
        inspectionArea,
        inspectionSheet,
        showErrorModal,
        getAccessToken,
        fetchInspectionItems,
        changeIsJobRunning,
        dispatch,
      ]),
    },
  }

  return {
    buttons: mode === Mode.Modelling ? buttonsCreate : buttonsUpdate,
  }
}
